import React, { FC, useEffect, useState, useMemo, MutableRefObject } from 'react';
import { Source, Layer, useMap } from 'react-map-gl';
import type { MapRef } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import { clusterLayerPFV, clusterCountLayerPFV, clusterShadowLayerPFV } from '../../../../components/property-map-viewer/MapLayersUtil';
import { MapboxMarker } from './MapboxMarker';
import { MapMarker } from '../../domain/entities/MapMarker';
import { mapMarkersToGeoJson } from '../../infrastructure/adapters/geoJsonAdapter';
import { useFilteredMarkers } from '../hooks/useFilteredMarkers';
import { useHoverStore } from '../../application/hoverStore';

type ClusteredSourceProps = {
  id?: string;
  clusterMaxZoom?: number;
  clusterRadius?: number;
  onMarkerClick?: (info: { id: string; property_id: string; latitude: number; longitude: number }) => void;
  markersRef?: MutableRefObject<Record<string, mapboxgl.Marker>>;
};

const ClusteredSource: FC<ClusteredSourceProps> = ({
  id = 'public-markers',
  clusterMaxZoom = 12,
  clusterRadius = 50,
  onMarkerClick,
  markersRef,
}) => {
  const { current: mapRef } = useMap() as { current: MapRef };
  const [ unclustered, setUnclustered ] = useState<GeoJSON.Feature[]>([]);
  const setHoveredPropertyId = useHoverStore(s => s.setHoveredPropertyId);
  const {
    markers,
    isLoading: isLoadingMarkers,
    error: errorMarkers
  } = useFilteredMarkers();

  const data = useMemo(() => mapMarkersToGeoJson(markers), [ markers ]);

  // This still runs a lot, but i dont think its that bad.
  const unclusteredMarkers = useMemo(() => {
    const unclusteredMarkers = unclustered
      .filter((f, index, self) =>
        // Remove duplicates based on property ID
        index === self.findIndex(item =>
          item.properties?.id === f.properties?.id
        )
      )
      .map(f => {
        const props = f.properties as any;
        const lat = props.latitude;
        const lng = props.longitude;

        if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
          console.warn('Skipping invalid marker', props);
          return null;
        }
        const marker: MapMarker = {
          propertyId: f.properties.id,
          lat: lat,
          lng: lng,
          listPrice: props.listPrice,
          saleOrRent: props.saleOrRent,
        };

        const handleClick = () => {
          if (onMarkerClick) {
            onMarkerClick({
              id: marker.propertyId,
              property_id: marker.propertyId,
              latitude: marker.lat,
              longitude: marker.lng,
            });
          }
        };

        const markerRefCallback = (ref: mapboxgl.Marker) => {
          if (ref && markersRef) {
            markersRef.current[marker.propertyId] = ref;
          }
        };

        const handleMouseEnter = () => {
          setHoveredPropertyId(marker.propertyId);
        };

        const handleMouseLeave = () => {
          setHoveredPropertyId(null);
        };

        return MapboxMarker(
          marker,
          false,
          handleClick,
          markerRefCallback,
          handleMouseEnter,
          handleMouseLeave
        );

      });

    return unclusteredMarkers;
  }, [ unclustered, setHoveredPropertyId ]);

  useEffect(() => {
    if (!mapRef) return;
    const handler = () => {
      const features = mapRef
        .getMap()
        .querySourceFeatures(id)
        .filter(f => !f.properties?.cluster);
      // these are the markers that are not clustered
      setUnclustered(features);
    };

    mapRef.getMap().on('render', handler);
    return () => {
      mapRef.getMap().off('render', handler);
    };
  }, [ mapRef, id ]);

  if (errorMarkers) return <div className="text-red-600">Failed to load properties.</div>;
  if (isLoadingMarkers) return <div>Loading…</div>;

  return (
    <Source
      id={id}
      type="geojson"
      data={data}
      cluster={true}
      clusterMaxZoom={clusterMaxZoom}
      clusterRadius={clusterRadius}
    >
      <Layer {...clusterLayerPFV} />
      <Layer {...clusterCountLayerPFV} />
      {/* <Layer {...clusterShadowLayerPFV} /> */}
      {unclusteredMarkers}
    </Source>
  );
};

export default ClusteredSource;
